import { getSupabase } from "./client";

export type PilotCostInvoiceLegRow = {
  id: string;
  invoice_id: string;
  flight_leg_id: string | null;
  leg_flight_date: string;
  leg_from_icao: string;
  leg_to_icao: string;
  leg_route_snippet: string;
};

export type PilotCostInvoiceRow = {
  id: string;
  aircraft_id: string;
  title: string;
  amount_usd: number;
  period_start: string;
  period_end: string;
  vendor_note: string;
  created_at: string;
  pilot_cost_invoice_legs?: PilotCostInvoiceLegRow[];
};

const LEG_SELECT = "id, flight_leg_id, leg_flight_date, leg_from_icao, leg_to_icao, leg_route_snippet";

export async function listPilotCostInvoices(): Promise<{
  data: PilotCostInvoiceRow[];
  error: string | null;
}> {
  const sb = getSupabase();
  if (!sb) return { data: [], error: null };

  const { data, error } = await sb
    .from("pilot_cost_invoices")
    .select(`*, pilot_cost_invoice_legs (${LEG_SELECT})`)
    .order("created_at", { ascending: false });

  if (error) return { data: [], error: error.message };
  return { data: (data ?? []) as PilotCostInvoiceRow[], error: null };
}

export type PilotCostInvoiceLegInsert = {
  flight_leg_id: string | null;
  leg_flight_date: string;
  leg_from_icao: string;
  leg_to_icao: string;
  leg_route_snippet: string;
};

export async function createPilotCostInvoiceWithLegs(
  invoice: {
    aircraft_id: string;
    title: string;
    amount_usd: number;
    period_start: string;
    period_end: string;
    vendor_note: string;
  },
  legs: PilotCostInvoiceLegInsert[]
): Promise<{ id: string | null; error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { id: null, error: "Supabase not configured" };

  const { data: inv, error: invErr } = await sb
    .from("pilot_cost_invoices")
    .insert({
      aircraft_id: invoice.aircraft_id,
      title: invoice.title,
      amount_usd: invoice.amount_usd,
      period_start: invoice.period_start,
      period_end: invoice.period_end,
      vendor_note: invoice.vendor_note,
    })
    .select("id")
    .single();

  if (invErr || !inv?.id) {
    return { id: null, error: invErr?.message ?? "Insert failed" };
  }

  const invoiceId = inv.id as string;
  const rows = legs.map((l) => ({
    invoice_id: invoiceId,
    flight_leg_id: l.flight_leg_id,
    leg_flight_date: l.leg_flight_date,
    leg_from_icao: l.leg_from_icao,
    leg_to_icao: l.leg_to_icao,
    leg_route_snippet: l.leg_route_snippet,
  }));

  const chunk = 200;
  for (let i = 0; i < rows.length; i += chunk) {
    const slice = rows.slice(i, i + chunk);
    const { error: legErr } = await sb.from("pilot_cost_invoice_legs").insert(slice);
    if (legErr) {
      await sb.from("pilot_cost_invoices").delete().eq("id", invoiceId);
      return { id: null, error: legErr.message };
    }
  }

  return { id: invoiceId, error: null };
}

export async function deletePilotCostInvoice(id: string): Promise<{ error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { error: "Supabase not configured" };

  const { error } = await sb.from("pilot_cost_invoices").delete().eq("id", id);
  return { error: error?.message ?? null };
}
